import {
  deleteV1CompaniesCompanyIdLabelsId,
  getV1CompaniesCompanyIdLabels,
  Label_Create,
  Label_Update,
  patchV1CompaniesCompanyIdLabelsId,
  postV1CompaniesCompanyIdLabels,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import rethrowIfAborted from '~utils/rethrowIfAborted';

import { Label } from './models';

interface FetchLabelsParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
  search?: string;
  signal?: AbortSignal;
}

interface CreateLabelParams {
  companyId: string;
  label: Label_Create;
}

interface UpdateLabelParams {
  companyId: string;
  label: Label_Update;
  labelId: string;
}

interface DeleteLabelParams {
  companyId: string;
  labelId: string;
}

interface FetchLabelsTypeaheadParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  query?: string;
}

export default function useLabels() {
  const fetchLabels = useCallback(
    async ({ companyId, link, cursor, limit, search, signal }: FetchLabelsParams) => {
      try {
        const response = await getV1CompaniesCompanyIdLabels({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit || DEFAULT_API_PAGE_SIZE,
            ...(search ? { 'search[query]': search } : {}),
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
          signal,
        });
        const labels = response.data.data.map((item) => Label.parse(item));
        const pagination = extractPagination(response);

        return { data: labels, pagination };
      } catch (error) {
        rethrowIfAborted(error, signal);

        connection.handleRequestError(error, t('error_messages.labels.failed_to_fetch'));
      }
    },
    [],
  );

  const createLabel = useCallback(async ({ companyId, label }: CreateLabelParams) => {
    try {
      const response = await postV1CompaniesCompanyIdLabels({
        path: { 'company-id': companyId },
        body: label,
      });
      const parsed = Label.parse(response.data.data);

      return parsed;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.labels.failed_to_create'));
    }
  }, []);

  const updateLabel = useCallback(
    async ({ companyId, label, labelId }: UpdateLabelParams) => {
      try {
        const response = await patchV1CompaniesCompanyIdLabelsId({
          path: { 'company-id': companyId, id: labelId },
          body: label,
        });
        const parsed = Label.parse(response.data.data);

        return parsed;
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.labels.failed_to_update'));
      }
    },
    [],
  );

  const deleteLabel = useCallback(async ({ companyId, labelId }: DeleteLabelParams) => {
    try {
      await deleteV1CompaniesCompanyIdLabelsId({
        path: { 'company-id': companyId, id: labelId },
      });

      return true;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.labels.failed_to_delete'));
    }
  }, []);

  const fetchLabelsTypeahead = async ({
    companyId,
    cursor,
    limit,
    query,
  }: FetchLabelsTypeaheadParams) => {
    try {
      // FIXME @todo TRE-12887 -> To be replaced with a proper typeahead endpoint when available
      const response = await getV1CompaniesCompanyIdLabels({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit || DEFAULT_API_PAGE_SIZE,
          ...(query ? { 'search[query]': query } : {}),
          ...(cursor ? { 'page[after]': cursor } : {}),
        },
      });
      const labels = response.data.data.map((item) => Label.parse(item));
      const pagination = extractPagination(response);

      return { data: labels, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.labels.failed_to_fetch'));
    }
  };

  return { createLabel, deleteLabel, fetchLabels, updateLabel, fetchLabelsTypeahead };
}
